import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import './DragSource.scss';

const propTypes = {

	/**
	 * The drag-and-drop type of the drag source.
	 */
	type: PropTypes.string.isRequired,

	/**
	 * The current item related to this drag source.
	 */
	item: PropTypes.shape({

		/**
		 * The unique ID of the item.
		 */
		id: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The current item content to render.
	 */
	children: PropTypes.node,

	/**
	 * Function to handle the items being moved.
	 */
	onMove: PropTypes.func.isRequired,

};

/**
 * Shared component to render a basic Drag Source, using react-dnd.
 *
 * @param {Object} props  The props.
 *
 * @returns {Object}      The rendered component.
 */
const DragSource = ({ type, item, children, onMove }) => {

	const [dragProps, connectDragSource, connectDragPreview] = useDrag({
		item: {
			type,
			data: Object.assign({}, item),
		},
		end: (draggedItem, monitor) => {

			const dropResult = monitor.getDropResult();

			if (dropResult && (dropResult.prevItem || dropResult.nextItem)) {

				// The item that the dropped item was placed after (if any)
				const prevItem = dropResult.prevItem;

				// The item that the dropped item was placed before (if any)
				const nextItem = dropResult.nextItem;

				// Perform the move!
				onMove(draggedItem.data.id, prevItem, nextItem);

			}

		},
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	// Disable the browser default for the drag preview
	// i.e. if you don't provide a custom drag preview, there won't be one!
	useEffect(() => {
		connectDragPreview(getEmptyImage());
	});

	return (
		<div className={`tc-drag-source ${dragProps.isDragging ? 'tc-drag-source-is-dragging' : ''}`}>
			{React.cloneElement(children, { connectDragSource, connectDragPreview })}
		</div>
	);

};

DragSource.propTypes = propTypes;

export default DragSource;
